'use client';

import { fetchDeploys } from '@/services/deploys';
import { fetchPages } from '@/services/pages';
import { fetchTenant } from '@/services/tenants';
import { getQueryParam } from '@/utils/url';
import { useTenantSelection } from '@payloadcms/plugin-multi-tenant/client';
import { useField, useLocale } from '@payloadcms/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { buildPreviewUrl, updatePreviewIframe } from './update-preview-iframe';
import { useIframeLoadTracker } from './use-iframe-load-tracker';
import { usePreviewToolbarPortal } from './use-preview-toolbar-portal';

import type { TextFieldClientComponent } from 'payload';

import './DeployPageSelector.scss';

const DEPLOY_PARAM = 'livePreviewDeploy';
const PAGE_PARAM = 'livePreviewPage';
const WRAPPER_ID = 'fv-live-preview-selector-wrapper';

interface DeployOption {
  id: string;
  bucket: string;
}

interface PageOption {
  id: string;
  path: string;
}

const persistSelection = (deployId: string, pageId: string): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set(DEPLOY_PARAM, deployId);
  url.searchParams.set(PAGE_PARAM, pageId);
  window.history.replaceState({}, '', url.toString());
};

const applyToIframe = (
  deployId: string,
  pageId: string,
  deploys: DeployOption[],
  pages: PageOption[],
  tenantName: string,
  locale: string,
): void => {
  const deploy = deploys.find((d) => d.id === deployId);
  const page = pages.find((p) => p.id === pageId);
  if (!deploy || !page || !tenantName) return;

  const iframe = document.querySelector('.live-preview-window iframe') as HTMLIFrameElement | null;
  if (!iframe?.src) return;

  updatePreviewIframe(
    buildPreviewUrl({
      currentSrc: iframe.src,
      path: page.path,
      tenantName,
      bucket: deploy.bucket,
      locale,
    }),
  );
};

const DeployPageSelector: TextFieldClientComponent = ({ path }) => {
  const { setValue } = useField<string>({ path });
  const { selectedTenantID } = useTenantSelection();
  const tenantId = selectedTenantID ? String(selectedTenantID) : null;
  const { code: localeCode } = useLocale();

  const { isLoading, startLoading } = useIframeLoadTracker();
  const portalTarget = usePreviewToolbarPortal(WRAPPER_ID);

  const [deploys, setDeploys] = useState<DeployOption[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [selectedDeployId, setSelectedDeployId] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isDeployOpen, setIsDeployOpen] = useState(false);
  const [isPageOpen, setIsPageOpen] = useState(false);

  // Refs hold the latest deploys/tenantName so async callbacks and handlers
  // always read current values without adding them to effect dependency arrays.
  const deploysRef = useRef<DeployOption[]>([]);
  const tenantNameRef = useRef('');

  const apply = useCallback(
    (deployId: string, pageId: string, pageList: PageOption[]) => {
      startLoading();
      setValue(`${deployId}:${pageId}`, true);
      persistSelection(deployId, pageId);
      applyToIframe(deployId, pageId, deploysRef.current, pageList, tenantNameRef.current, localeCode);
    },
    [startLoading, setValue, localeCode],
  );

  useEffect(() => {
    if (!tenantId || !portalTarget) return;
    let cancelled = false;

    const load = async () => {
      const [deployDocs, tenant] = await Promise.all([fetchDeploys(tenantId), fetchTenant(tenantId)]);
      if (cancelled) return;

      const deploys = deployDocs.map(({ id, bucket }) => ({ id, bucket }));
      setDeploys(deploys);
      deploysRef.current = deploys;
      tenantNameRef.current = tenant.name;

      const saved = getQueryParam(DEPLOY_PARAM);
      const initial = (saved && deploys.find((d) => d.id === saved) ? saved : deploys[0]?.id) ?? null;
      setSelectedDeployId(initial);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, portalTarget]);

  useEffect(() => {
    if (!selectedDeployId || !tenantId || !portalTarget) return;
    const deployId = selectedDeployId;
    const tid = tenantId;
    let cancelled = false;

    const load = async () => {
      setPages([]);
      const pageDocs = await fetchPages(deployId, tid, localeCode);
      if (cancelled || !pageDocs.length) return;

      const pages = pageDocs.map(({ id, path }) => ({ id, path }));

      setPages(pages);
      const saved = getQueryParam(PAGE_PARAM);
      const pageId = (saved && pages.find((p) => p.id === saved) ? saved : pages[0].id) ?? null;
      setSelectedPageId(pageId);
      if (pageId) apply(deployId, pageId, pages);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDeployId, tenantId, localeCode, apply, portalTarget]);

  const handleDeploySelect = (deployId: string) => {
    if (deployId === selectedDeployId) {
      setIsDeployOpen(false);
      return;
    }
    setSelectedDeployId(deployId);
    setSelectedPageId(null);
    setIsDeployOpen(false);
  };

  const handlePageSelect = (pageId: string) => {
    if (pageId === selectedPageId) {
      setIsPageOpen(false);
      return;
    }
    setSelectedPageId(pageId);
    setIsPageOpen(false);
    if (selectedDeployId) apply(selectedDeployId, pageId, pages);
  };

  const selectedDeploy = deploys.find((d) => d.id === selectedDeployId);
  const selectedPage = pages.find((p) => p.id === selectedPageId);

  if (!tenantId || !deploys.length) return null;

  const selectorUI = (
    <div className="fv-live-preview-selector">
      <div className="fv-live-preview-selector__group">
        <button
          type="button"
          className={['fv-live-preview-selector__trigger', isLoading && 'fv-live-preview-selector__trigger--loading']
            .filter(Boolean)
            .join(' ')}
          onClick={() => {
            setIsDeployOpen(!isDeployOpen);
            setIsPageOpen(false);
          }}
        >
          <span className="fv-live-preview-selector__label">
            {isLoading && !isDeployOpen ? (
              <span className="fv-live-preview-selector__loading-wrapper">
                <span className="fv-live-preview-selector__spinner" />
                Loading...
              </span>
            ) : (
              <span className="fv-live-preview-selector__label-text">{selectedDeploy?.bucket ?? 'Select deploy'}</span>
            )}
          </span>
          <span className="fv-live-preview-selector__chevron">{isDeployOpen ? '▲' : '▼'}</span>
        </button>

        {isDeployOpen && (
          <>
            <div className="fv-live-preview-selector__backdrop" onClick={() => setIsDeployOpen(false)} />
            <div className="fv-live-preview-selector__dropdown">
              {deploys.map((deploy) => (
                <button
                  key={deploy.id}
                  type="button"
                  className={[
                    'fv-live-preview-selector__option',
                    deploy.id === selectedDeployId && 'fv-live-preview-selector__option--selected',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleDeploySelect(deploy.id)}
                >
                  {deploy.bucket}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="fv-live-preview-selector__separator" />

      <div className="fv-live-preview-selector__group">
        <button
          type="button"
          className="fv-live-preview-selector__trigger"
          disabled={!pages.length}
          onClick={() => {
            setIsPageOpen(!isPageOpen);
            setIsDeployOpen(false);
          }}
        >
          <span className="fv-live-preview-selector__label">
            <span className="fv-live-preview-selector__label-text">{selectedPage?.path ?? 'Select page'}</span>
          </span>
          <span className="fv-live-preview-selector__chevron">{isPageOpen ? '▲' : '▼'}</span>
        </button>

        {isPageOpen && (
          <>
            <div className="fv-live-preview-selector__backdrop" onClick={() => setIsPageOpen(false)} />
            <div className="fv-live-preview-selector__dropdown">
              {pages.map((page) => (
                <button
                  key={page.id}
                  type="button"
                  className={[
                    'fv-live-preview-selector__option',
                    page.id === selectedPageId && 'fv-live-preview-selector__option--selected',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handlePageSelect(page.id)}
                >
                  {page.path}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (portalTarget) return createPortal(selectorUI, portalTarget);
  return null;
};

export default DeployPageSelector;
